import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api, apiFetch } from './client';
import { countsOf, parseItemPreview } from './format';
import { reorderItems } from './sections';
import { haptic } from '../telegram/sdk';
import type {
  ActiveList,
  ClearDoneResult,
  Invite,
  InvitePreview,
  Item,
  ItemMove,
  ListDetail,
  ListSummary,
  Me,
  Member,
  RenameCategoryResult,
  Role,
  SearchHit,
} from './types';

/**
 * React Query bindings for the routes in `types.ts`.
 *
 * Two things live here rather than in the screens:
 *
 * 1. **Optimistic writes.** Toggle / add / delete apply to the cache first and
 *    roll back on failure. The rollback is the interesting half — a viewer's
 *    forged write gets a 403 from `service.py`, and the row has to snap back
 *    rather than sit there looking saved.
 * 2. **Haptics.** `haptic()` resolves to a no-op unless `window.Telegram.WebApp`
 *    handed us a real `initData`, so putting the calls on the mutations gives
 *    "Telegram only" for free, with no `isTelegram` branch at any call site.
 *
 * A list's meta and its items are two queries against two endpoints, matching
 * the contract. `useListDetail` stitches them for the screens.
 */

export const queryKeys = {
  me: ['me'] as const,
  lists: ['lists'] as const,
  list: (id: number) => ['lists', id] as const,
  items: (id: number) => ['lists', id, 'items'] as const,
  members: (id: number) => ['lists', id, 'members'] as const,
  search: (q: string) => ['search', q] as const,
  invite: (token: string) => ['invites', token] as const,
  active: ['active'] as const,
};

// ------------------------------------------------------------------- reads

export function useMe() {
  return useQuery({ queryKey: queryKeys.me, queryFn: () => api.get<Me>('/api/me') });
}

export function useLists() {
  return useQuery({ queryKey: queryKeys.lists, queryFn: () => api.get<ListSummary[]>('/api/lists') });
}

export function useList(id: number | null) {
  return useQuery({
    queryKey: queryKeys.list(id ?? -1),
    queryFn: () => api.get<ListSummary>(`/api/lists/${id}`),
    enabled: id !== null,
  });
}

export function useListItems(id: number | null) {
  return useQuery({
    queryKey: queryKeys.items(id ?? -1),
    queryFn: () => api.get<Item[]>(`/api/lists/${id}/items`),
    enabled: id !== null,
  });
}

export interface ListDetailResult {
  list: ListDetail | null;
  isPending: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Meta + items as one view model.
 *
 * `isPending` is the union and `error` is whichever query failed first, so an
 * inaccessible list produces a single 404 for the screen to render — not a
 * half-loaded page with a spinner stuck in the item column.
 */
export function useListDetail(id: number | null): ListDetailResult {
  const meta = useList(id);
  const items = useListItems(id);

  const list: ListDetail | null =
    meta.data && items.data ? { ...meta.data, ...countsOf(items.data), items: items.data } : null;

  return {
    list,
    isPending: meta.isPending || items.isPending,
    isError: meta.isError || items.isError,
    error: meta.error ?? items.error ?? null,
    refetch: () => {
      void meta.refetch();
      void items.refetch();
    },
  };
}

export function useMembers(id: number | null) {
  return useQuery({
    queryKey: queryKeys.members(id ?? -1),
    queryFn: () => api.get<Member[]>(`/api/lists/${id}/members`),
    enabled: id !== null,
  });
}

export function useSearch(q: string) {
  const term = q.trim();
  return useQuery({
    queryKey: queryKeys.search(term),
    queryFn: () => api.get<SearchHit[]>('/api/search', { query: { q: term, limit: 50 } }),
    enabled: term.length > 0,
    // Results are keyed by term, so the previous term's hits stay on screen
    // while the next request is in flight instead of flashing a skeleton.
    placeholderData: (previous) => previous,
  });
}

export function useInvitePreview(token: string | null) {
  return useQuery({
    queryKey: queryKeys.invite(token ?? ''),
    queryFn: () => api.get<InvitePreview>(`/api/invites/${token}`),
    enabled: !!token,
  });
}

export function useActiveList() {
  return useQuery({ queryKey: queryKeys.active, queryFn: () => api.get<ActiveList>('/api/active') });
}

// --------------------------------------------------------- cache plumbing

/**
 * Writes an items cache entry and keeps the list's counters in step.
 *
 * The overview reads `itemCount` / `doneCount` from `GET /api/lists`, so an
 * optimistic toggle that only touched the items cache would leave "4 done"
 * showing while the checkbox says 5. Both caches are corrected together and
 * both are snapshotted for rollback.
 */
interface Snapshot {
  items: Item[] | undefined;
  lists: ListSummary[] | undefined;
  meta: ListSummary | undefined;
}

function snapshot(qc: QueryClient, listId: number): Snapshot {
  return {
    items: qc.getQueryData<Item[]>(queryKeys.items(listId)),
    lists: qc.getQueryData<ListSummary[]>(queryKeys.lists),
    meta: qc.getQueryData<ListSummary>(queryKeys.list(listId)),
  };
}

function restore(qc: QueryClient, listId: number, snap: Snapshot | undefined) {
  if (!snap) return;
  qc.setQueryData(queryKeys.items(listId), snap.items);
  qc.setQueryData(queryKeys.lists, snap.lists);
  qc.setQueryData(queryKeys.list(listId), snap.meta);
}

function writeItems(qc: QueryClient, listId: number, update: (items: Item[]) => Item[]) {
  const current = qc.getQueryData<Item[]>(queryKeys.items(listId));
  if (!current) return;
  const next = update(current);
  qc.setQueryData(queryKeys.items(listId), next);

  const counts = countsOf(next);
  qc.setQueryData<ListSummary[]>(queryKeys.lists, (lists) =>
    lists?.map((l) => (l.id === listId ? { ...l, ...counts } : l)),
  );
  qc.setQueryData<ListSummary>(queryKeys.list(listId), (meta) =>
    meta ? { ...meta, ...counts } : meta,
  );
}

/** Everything a write to `listId` can have invalidated. */
function invalidateList(qc: QueryClient, listId: number) {
  void qc.invalidateQueries({ queryKey: queryKeys.items(listId) });
  void qc.invalidateQueries({ queryKey: queryKeys.list(listId) });
  void qc.invalidateQueries({ queryKey: queryKeys.lists });
  void qc.invalidateQueries({ queryKey: ['search'] });
}

/** Temp ids for optimistic rows. Negative so they can never collide with the DB. */
let tempId = 0;
const nextTempId = () => --tempId;

// ------------------------------------------------------------------ lists

export function useCreateList() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => api.post<ListSummary>('/api/lists', { name }),
    onMutate: () => {
      void haptic('light');
    },
    onSuccess: () => {
      void haptic('success');
      void qc.invalidateQueries({ queryKey: queryKeys.lists });
    },
    onError: () => void haptic('error'),
  });
}

export function useRenameList(id: number) {
  const qc = useQueryClient();
  return useMutation<ListSummary, Error, string, Snapshot>({
    mutationFn: (name: string) => api.patch<ListSummary>(`/api/lists/${id}`, { name }),
    onMutate: (name) => {
      void haptic('light');
      const snap = snapshot(qc, id);
      qc.setQueryData<ListSummary[]>(queryKeys.lists, (lists) =>
        lists?.map((l) => (l.id === id ? { ...l, name } : l)),
      );
      qc.setQueryData<ListSummary>(queryKeys.list(id), (meta) => (meta ? { ...meta, name } : meta));
      return snap;
    },
    onError: (_error, _name, snap) => {
      void haptic('error');
      restore(qc, id, snap);
    },
    onSuccess: () => void haptic('success'),
    onSettled: () => invalidateList(qc, id),
  });
}

export function useDeleteList(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<void>(`/api/lists/${id}`),
    onMutate: () => {
      void haptic('medium');
    },
    onSuccess: () => {
      void haptic('success');
      qc.removeQueries({ queryKey: queryKeys.list(id) });
      qc.removeQueries({ queryKey: queryKeys.items(id) });
      void qc.invalidateQueries({ queryKey: queryKeys.lists });
      void qc.invalidateQueries({ queryKey: ['search'] });
    },
    onError: () => void haptic('error'),
  });
}

// ------------------------------------------------------------------ items

export function useAddItem(listId: number) {
  const qc = useQueryClient();
  return useMutation<Item, Error, string, Snapshot & { tempId: number }>({
    mutationFn: (text: string) => api.post<Item>(`/api/lists/${listId}/items`, { text }),
    onMutate: async (text) => {
      void haptic('light');
      await qc.cancelQueries({ queryKey: queryKeys.items(listId) });
      const snap = snapshot(qc, listId);
      const id = nextTempId();
      // The server re-parses `#tag`; this preview only decides which section
      // the row appears in for the few hundred ms before the real item lands.
      const optimistic: Item = { id, listId, done: false, ...parseItemPreview(text) };
      writeItems(qc, listId, (items) => [...items, optimistic]);
      return { ...snap, tempId: id };
    },
    onError: (_error, _text, context) => {
      void haptic('error');
      restore(qc, listId, context);
    },
    onSuccess: (created, _text, context) => {
      // Swap the placeholder for the server's row *in place*, so the item does
      // not jump when its real category arrives.
      writeItems(qc, listId, (items) =>
        items.map((i) => (i.id === context?.tempId ? created : i)),
      );
    },
    onSettled: () => invalidateList(qc, listId),
  });
}

export function useToggleItem(listId: number) {
  const qc = useQueryClient();
  return useMutation<Item, Error, { itemId: number; done: boolean }, Snapshot>({
    mutationFn: ({ itemId, done }) => api.patch<Item>(`/api/items/${itemId}`, { done }),
    onMutate: async ({ itemId, done }) => {
      void haptic('selection');
      await qc.cancelQueries({ queryKey: queryKeys.items(listId) });
      const snap = snapshot(qc, listId);
      writeItems(qc, listId, (items) =>
        items.map((i) => (i.id === itemId ? { ...i, done } : i)),
      );
      return snap;
    },
    onError: (_error, _vars, snap) => {
      void haptic('error');
      restore(qc, listId, snap);
    },
    onSettled: () => invalidateList(qc, listId),
  });
}

/** A partial patch, exactly as `PATCH /api/items/{id}` takes it. */
export interface ItemPatch {
  itemId: number;
  text?: string;
  category?: string | null;
}

/**
 * Edit an item in place — its wording, its category, or both in one request.
 *
 * Both fields go through one hook because the server takes them in one PATCH,
 * and because sending them separately would mean two optimistic writes and two
 * rollbacks for what the user did as a single edit.
 *
 * The text is sent **verbatim**: no `parseItemPreview` here. `service.set_text`
 * deliberately does not re-parse `#tag` — editing 'milk' to 'oat milk #food' on
 * an item already tagged 'drinks' expresses two intents, and only one of them
 * is a rename — so a client that split the text would be the divergent half of
 * exactly the client/server parse split CLAUDE.md warns about. A caller who
 * wants both sends `category` too.
 */
export function useUpdateItem(listId: number) {
  const qc = useQueryClient();
  return useMutation<Item, Error, ItemPatch, Snapshot>({
    mutationFn: ({ itemId, ...fields }) => api.patch<Item>(`/api/items/${itemId}`, fields),
    onMutate: async ({ itemId, ...fields }) => {
      void haptic('light');
      await qc.cancelQueries({ queryKey: queryKeys.items(listId) });
      const snap = snapshot(qc, listId);
      // Only the keys actually sent: spreading `{ text: undefined }` over the
      // row would blank a field the caller never mentioned. `category: null`
      // survives this, because clearing a category is a real edit.
      const patch = Object.fromEntries(
        Object.entries(fields).filter(([, value]) => value !== undefined),
      );
      writeItems(qc, listId, (items) =>
        items.map((i) => (i.id === itemId ? { ...i, ...patch } : i)),
      );
      return snap;
    },
    onError: (_error, _vars, snap) => {
      void haptic('error');
      restore(qc, listId, snap);
    },
    onSettled: () => invalidateList(qc, listId),
  });
}

/** `{ itemId }` plus the body of `POST /api/items/{id}/move`. */
export interface ItemMoveVars extends ItemMove {
  itemId: number;
}

/**
 * Step an item up or down inside its `(category, done)` section.
 *
 * **Optimistic, unlike `useRenameCategory`.** The comparison is worth drawing:
 * a rename asks the server a question whose answer depends on the list's other
 * rows, so guessing it means reimplementing `_canonical_category`. A move does
 * not — the caller named the exact place the row goes, and `reorderItems`
 * applies that one instruction to the array the server already sent. There is
 * no rule being copied, so there is nothing to diverge.
 *
 * The row also has to move under the finger. A reorder that waited for a round
 * trip would leave the item sitting where it was for long enough to look like
 * a dropped tap, and the second tap would then be computed against stale
 * neighbours.
 *
 * **No toast.** A move reports itself: the row is somewhere else. The call site
 * passes `onError`, which is where a *failed* move needs a sentence, since the
 * rollback silently puts the row back where it started.
 */
export function useMoveItem(listId: number) {
  const qc = useQueryClient();
  return useMutation<Item, Error, ItemMoveVars, Snapshot>({
    mutationFn: ({ itemId, afterId }) => api.post<Item>(`/api/items/${itemId}/move`, { afterId }),
    onMutate: async ({ itemId, afterId }) => {
      void haptic('selection');
      await qc.cancelQueries({ queryKey: queryKeys.items(listId) });
      const snap = snapshot(qc, listId);
      writeItems(qc, listId, (items) => reorderItems(items, itemId, afterId));
      return snap;
    },
    onError: (_error, _vars, snap) => {
      void haptic('error');
      restore(qc, listId, snap);
    },
    onSettled: () => invalidateList(qc, listId),
  });
}

export function useDeleteItem(listId: number) {
  const qc = useQueryClient();
  return useMutation<void, Error, number, Snapshot>({
    mutationFn: (itemId: number) => api.delete<void>(`/api/items/${itemId}`),
    onMutate: async (itemId) => {
      void haptic('medium');
      await qc.cancelQueries({ queryKey: queryKeys.items(listId) });
      const snap = snapshot(qc, listId);
      writeItems(qc, listId, (items) => items.filter((i) => i.id !== itemId));
      return snap;
    },
    onError: (_error, _itemId, snap) => {
      void haptic('error');
      restore(qc, listId, snap);
    },
    onSettled: () => invalidateList(qc, listId),
  });
}

export function useClearDone(listId: number) {
  const qc = useQueryClient();
  return useMutation<ClearDoneResult, Error, void, Snapshot>({
    mutationFn: () => api.post<ClearDoneResult>(`/api/lists/${listId}/clear-done`),
    onMutate: async () => {
      void haptic('medium');
      await qc.cancelQueries({ queryKey: queryKeys.items(listId) });
      const snap = snapshot(qc, listId);
      writeItems(qc, listId, (items) => items.filter((i) => !i.done));
      return snap;
    },
    onError: (_error, _vars, snap) => {
      void haptic('error');
      restore(qc, listId, snap);
    },
    onSettled: () => invalidateList(qc, listId),
  });
}

/** `{ from, to }`, exactly as `POST /api/lists/{id}/rename-category` takes it. */
export interface CategoryRename {
  from: string;
  /** The new spelling, or `null` to strip the tag off every item that has it. */
  to: string | null;
}

/**
 * Rename a category across a list, or clear it — the bot's `/cat old | new`.
 *
 * **Deliberately not optimistic**, unlike every other write here. Guessing the
 * result would mean reimplementing `_canonical_category` in TypeScript: renaming
 * into a spelling the list already holds adopts *that* spelling and merges two
 * sections into one, so the client would have to decide which of `Food` and
 * `food` survives and how the merged section orders. That is the
 * second-copy-of-a-rule failure this codebase keeps naming — one wrong guess
 * here is not a mislabelled row for a frame but a whole section rebuilt wrongly.
 * A rename is also rare and deliberate, so the round trip costs nothing anyone
 * is drumming their fingers over. Invalidate, re-read, render what the server
 * actually did.
 */
export function useRenameCategory(listId: number) {
  const qc = useQueryClient();
  return useMutation<RenameCategoryResult, Error, CategoryRename>({
    mutationFn: (rename) =>
      api.post<RenameCategoryResult>(`/api/lists/${listId}/rename-category`, rename),
    onMutate: () => void haptic('medium'),
    onSuccess: () => void haptic('success'),
    onError: () => void haptic('error'),
    onSettled: () => invalidateList(qc, listId),
  });
}

// --------------------------------------------------------------- the active list

/**
 * The active list is shared with the bot (`user_state`), which is the whole
 * point of exposing it: set it here and the next inline `@bot buy milk` lands
 * in the list the web UI is showing.
 */
export function useSetActiveList() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (listId: number) => api.post<ActiveList>('/api/active', { listId }),
    onMutate: () => void haptic('light'),
    onSuccess: (active) => {
      void haptic('success');
      qc.setQueryData(queryKeys.active, active);
    },
    onError: () => void haptic('error'),
  });
}

// -------------------------------------------------------------------- sharing

export function useCreateInvite(listId: number) {
  return useMutation({
    mutationFn: (role: Role) => api.post<Invite>(`/api/lists/${listId}/invites`, { role }),
    onSuccess: () => void haptic('success'),
    onError: () => void haptic('error'),
  });
}

export function useAddMember(listId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, role }: { userId: number; role: Role }) =>
      api.post<Member>(`/api/lists/${listId}/members`, { userId, role }),
    onSuccess: () => {
      void haptic('success');
      void qc.invalidateQueries({ queryKey: queryKeys.members(listId) });
    },
    onError: () => void haptic('error'),
  });
}

export function useRemoveMember(listId: number) {
  const qc = useQueryClient();
  return useMutation<void, Error, number, { members: Member[] | undefined }>({
    mutationFn: (userId: number) =>
      api.delete<void>(`/api/lists/${listId}/members`, { query: { userId } }),
    onMutate: async (userId) => {
      void haptic('medium');
      await qc.cancelQueries({ queryKey: queryKeys.members(listId) });
      const previous = qc.getQueryData<Member[]>(queryKeys.members(listId));
      qc.setQueryData<Member[]>(queryKeys.members(listId), (members) =>
        members?.filter((m) => m.userId !== userId),
      );
      return { members: previous };
    },
    onError: (_error, _userId, context) => {
      void haptic('error');
      qc.setQueryData(queryKeys.members(listId), context?.members);
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: queryKeys.members(listId) }),
  });
}

export function useRedeemInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (token: string) => api.post<ListSummary>(`/api/invites/${token}`),
    onSuccess: () => {
      void haptic('success');
      void qc.invalidateQueries({ queryKey: queryKeys.lists });
    },
    onError: () => void haptic('error'),
  });
}

// --------------------------------------------------------------------- export

/**
 * `/api/export` needs the auth header like every other route, so it cannot be
 * handed to `window.open` — that request would arrive unauthenticated and 401.
 * Fetch it properly, then hand the browser a Blob to save.
 */
export async function downloadExport(): Promise<void> {
  const payload = await apiFetch<unknown>('/api/export');
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const href = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = href;
    link.download = `manager-export-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    // Revoking synchronously can cancel the download in Safari; one tick is enough.
    setTimeout(() => URL.revokeObjectURL(href), 0);
  }
}
